import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Wallet, Receipt, CalendarRange, Plus, Trash2, ArrowDownCircle, ArrowUpCircle,
  FileSpreadsheet, FileText, TrendingUp, TrendingDown, AlertTriangle,
} from "lucide-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Bar, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { api, rd, inputCls, labelCls } from "./posShared";

const EXPENSE_CATEGORIES = [
  { key: "delivery", label: "Delivery / Mensajería" },
  { key: "compras", label: "Compras / Inventario" },
  { key: "publicidad", label: "Publicidad" },
  { key: "empaques", label: "Empaques / Materiales" },
  { key: "servicios", label: "Servicios (luz, internet)" },
  { key: "sueldos", label: "Sueldos / Comisiones" },
  { key: "general", label: "Otros" },
];
const catLabel = (k) => (EXPENSE_CATEGORIES.find((c) => c.key === k) || {}).label || k;
const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthName = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("es-DO", { month: "long", year: "numeric" });
};

export default function CajaChica() {
  const [tab, setTab] = useState("gastos");
  return (
    <div>
      <h1 className="font-serif text-3xl mb-2">Caja chica y gastos</h1>
      <p className="text-sm text-neutral-500 mb-6">Registra los gastos de la tienda, controla la caja chica y genera el cierre mensual.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="cajachica-tabs">
        {[
          { key: "gastos", label: "Gastos", icon: Receipt },
          { key: "caja", label: "Caja chica", icon: Wallet },
          { key: "cierre", label: "Cierre mensual", icon: CalendarRange },
        ].map((t) => (
          <button key={t.key} data-testid={`cajachica-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.1em] border transition ${tab === t.key ? "bg-espresso text-cream border-espresso" : "border-neutral-200 text-neutral-500 hover:border-espresso"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "gastos" && <Gastos />}
      {tab === "caja" && <PettyCash />}
      {tab === "cierre" && <CierreMensual />}
    </div>
  );
}

/* ------------------------------ GASTOS ------------------------------ */
function Gastos() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState({ date: today(), category: "delivery", description: "", amount: "", source: "caja_chica", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/pos/expenses?month=${month}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/pos/petty-cash").then((r) => setBalance(r.data.balance)).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const amountNum = Number(form.amount) || 0;
  const willGoNegative = form.source === "caja_chica" && balance !== null && amountNum > balance;

  const save = async () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return toast.error("Ingresa un monto válido");
    if (willGoNegative && !window.confirm(`Este gasto dejará la caja chica en negativo (saldo actual: ${rd(balance)}). Deberás reponerla. ¿Continuar?`)) return;
    setSaving(true);
    try {
      await api.post("/pos/expenses", { ...form, amount });
      toast.success("Gasto registrado");
      if (form.source === "caja_chica" && amount > balance) toast.warning("La caja chica quedó en negativo. Recuerda reponerla.");
      setForm({ date: today(), category: "delivery", description: "", amount: "", source: "caja_chica", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm("¿Eliminar este gasto? Si salió de caja chica, se devolverá el monto.")) return;
    try {
      await api.delete(`/pos/expenses/${e.id}`);
      toast.success("Gasto eliminado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="bg-white border border-neutral-200 rounded-xl p-5 h-fit">
        <h2 className="font-medium mb-4">Registrar gasto</h2>
        <div className="space-y-3">
          <div><label className={labelCls}>Fecha</label><input data-testid="gasto-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Categoría</label>
            <select data-testid="gasto-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Descripción</label><input data-testid="gasto-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Ej: Pago mensajero pedido #123" className={inputCls} /></div>
          <div><label className={labelCls}>Monto (RD$)</label><input data-testid="gasto-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>¿De dónde salió el dinero?</label>
            <select data-testid="gasto-source" value={form.source} onChange={(e) => set("source", e.target.value)} className={inputCls}>
              <option value="caja_chica">Caja chica (descuenta del saldo)</option>
              <option value="otro">Otro (efectivo / banco)</option>
            </select>
          </div>
          {form.source === "caja_chica" && balance !== null && (
            <p className={`text-xs ${balance < 0 ? "text-red-600" : "text-neutral-500"}`} data-testid="gasto-balance-hint">
              Saldo de caja chica: <span className="font-medium">{rd(balance)}</span>
            </p>
          )}
          {willGoNegative && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="gasto-negative-warning">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>Este gasto dejará la caja chica en negativo ({rd(balance - amountNum)}). Considera reponerla.</span>
            </div>
          )}
          <button data-testid="gasto-save" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.12em] hover:bg-rose transition disabled:opacity-60"><Plus size={15} /> {saving ? "Guardando..." : "Agregar gasto"}</button>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div><label className={labelCls}>Mes</label><input data-testid="gastos-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} /></div>
          {data && <div className="text-right"><p className="text-[11px] uppercase tracking-wider text-neutral-500">Total de gastos</p><p className="text-2xl font-serif text-rose" data-testid="gastos-total">{rd(data.total)}</p></div>}
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
              <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Categoría</th><th className="px-4 py-3">Descripción</th><th className="px-4 py-3">Fuente</th><th className="px-4 py-3 text-right">Monto</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {(data?.expenses || []).map((e) => (
                <tr key={e.id} data-testid={`gasto-row-${e.id}`} className="border-b border-neutral-50 hover:bg-neutral-50/60">
                  <td className="px-4 py-3 text-neutral-500 text-xs">{e.date}</td>
                  <td className="px-4 py-3">{catLabel(e.category)}</td>
                  <td className="px-4 py-3 text-neutral-600">{e.description || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${e.source === "caja_chica" ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-500"}`}>{e.source === "caja_chica" ? "Caja chica" : "Otro"}</span></td>
                  <td className="px-4 py-3 text-right font-medium text-rose">{rd(e.amount)}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`gasto-delete-${e.id}`} onClick={() => remove(e)} className="text-neutral-300 hover:text-red-500"><Trash2 size={15} /></button></td>
                </tr>
              ))}
              {(!data?.expenses || data.expenses.length === 0) && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-400">Sin gastos registrados este mes.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- CAJA CHICA ---------------------------- */
function PettyCash() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ date: today(), type: "entrada", reason: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/pos/petty-cash").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return toast.error("Ingresa un monto válido");
    setSaving(true);
    try {
      await api.post("/pos/petty-cash", { ...form, amount });
      toast.success(form.type === "entrada" ? "Entrada registrada" : "Salida registrada");
      setForm({ date: today(), type: "entrada", reason: "", amount: "", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm("¿Eliminar este movimiento de caja chica?")) return;
    try {
      await api.delete(`/pos/petty-cash/${m.id}`);
      toast.success("Movimiento eliminado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="space-y-6 h-fit">
        <div className={`rounded-xl p-5 text-cream ${data && data.balance < 0 ? "bg-red-600" : "bg-espresso"}`} data-testid="pettycash-balance-card">
          <p className="text-[11px] uppercase tracking-wider opacity-70">Saldo actual de caja chica</p>
          <p className="text-3xl font-serif mt-1" data-testid="pettycash-balance">{data ? rd(data.balance) : "…"}</p>
          {data && data.balance < 0 && <p className="text-xs opacity-80 mt-1 flex items-center gap-1"><AlertTriangle size={12} /> Caja chica en negativo — repón fondos.</p>}
        </div>
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="font-medium mb-4">Nuevo movimiento</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="pettycash-type-entrada" onClick={() => set("type", "entrada")} className={`inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs uppercase tracking-[0.08em] border transition ${form.type === "entrada" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-neutral-200 text-neutral-500"}`}><ArrowDownCircle size={14} /> Entrada</button>
              <button data-testid="pettycash-type-salida" onClick={() => set("type", "salida")} className={`inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs uppercase tracking-[0.08em] border transition ${form.type === "salida" ? "bg-rose/10 border-rose text-rose" : "border-neutral-200 text-neutral-500"}`}><ArrowUpCircle size={14} /> Salida</button>
            </div>
            <div><label className={labelCls}>Fecha</label><input data-testid="pettycash-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Concepto</label><input data-testid="pettycash-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder={form.type === "entrada" ? "Inversión / aporte" : "Retiro / pago"} className={inputCls} /></div>
            <div><label className={labelCls}>Monto (RD$)</label><input data-testid="pettycash-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className={inputCls} /></div>
            <button data-testid="pettycash-save" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.12em] hover:bg-rose transition disabled:opacity-60"><Plus size={15} /> {saving ? "Guardando..." : "Registrar movimiento"}</button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
            <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Concepto</th><th className="px-4 py-3 text-right">Monto</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {(data?.movements || []).map((m) => (
              <tr key={m.id} data-testid={`pettycash-row-${m.id}`} className="border-b border-neutral-50 hover:bg-neutral-50/60">
                <td className="px-4 py-3 text-neutral-500 text-xs">{m.date}</td>
                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${m.type === "entrada" ? "bg-emerald-50 text-emerald-700" : "bg-rose/10 text-rose"}`}>{m.type === "entrada" ? <ArrowDownCircle size={12} /> : <ArrowUpCircle size={12} />}{m.type}</span></td>
                <td className="px-4 py-3 text-neutral-600">{m.reason === "gasto" ? `Gasto: ${m.note}` : (m.reason || m.note || "—")}{m.related_expense_id && <span className="ml-2 text-[10px] text-amber-600">(auto)</span>}</td>
                <td className={`px-4 py-3 text-right font-medium ${m.type === "entrada" ? "text-emerald-600" : "text-rose"}`}>{m.type === "entrada" ? "+" : "−"} {rd(m.amount)}</td>
                <td className="px-4 py-3 text-right">{!m.related_expense_id && <button data-testid={`pettycash-delete-${m.id}`} onClick={() => remove(m)} className="text-neutral-300 hover:text-red-500"><Trash2 size={15} /></button>}</td>
              </tr>
            ))}
            {(!data?.movements || data.movements.length === 0) && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-400">Sin movimientos. Registra una entrada para fondear la caja chica.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------- CIERRE MENSUAL -------------------------- */
function CierreMensual() {
  const [mode, setMode] = useState("mes"); // mes | rango
  const [month, setMonth] = useState(thisMonth());
  const [range, setRange] = useState({ from: today().slice(0, 8) + "01", to: today() });
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);

  const query = mode === "rango" && range.from && range.to
    ? `frm=${range.from}&to=${range.to}`
    : `month=${month}`;
  const fileKey = mode === "rango" ? `${range.from}_${range.to}` : month;

  const load = useCallback(() => {
    api.get(`/pos/monthly-close?${query}`).then((r) => setData(r.data)).catch(() => {});
  }, [query]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/pos/monthly-trend?months=6").then((r) => setTrend(r.data)).catch(() => {});
  }, []);

  const label = mode === "rango" ? `${range.from} a ${range.to}` : monthName(month);
  const compare = trend.slice(-3);

  const exportXlsx = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const resumen = [
      ["Cierre — MO by Vir"],
      ["Período", label],
      [],
      ["Ventas totales", data.total_sales],
      ["Facturas", data.invoice_count],
      ["Delivery cobrado", data.delivery_total],
      ["ITBIS cobrado", data.itbis_total],
      ["Margen de ventas (bruto)", data.gross_profit],
      ["Gastos del mes", data.total_expenses],
      ["Ganancia neta", data.net_profit],
      [],
      ["Caja chica — Entradas", data.petty_cash_in],
      ["Caja chica — Salidas", data.petty_cash_out],
      ["Caja chica — Saldo actual", data.petty_cash_balance],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), "Resumen");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.expenses_by_category.map((c) => ({ Categoría: catLabel(c.category), Total: c.total }))), "Gastos por categoría");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.expenses.map((e) => ({ Fecha: e.date, Categoría: catLabel(e.category), Descripción: e.description, Fuente: e.source, Monto: e.amount }))), "Detalle de gastos");
    XLSX.writeFile(wb, `cierre-mensual-${fileKey}.xlsx`);
  };

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const M = 40;
    doc.setTextColor("#B76E79"); doc.setFontSize(22); doc.setFont("helvetica", "bold");
    doc.text("Cierre mensual — MO by Vir", M, 48);
    doc.setTextColor("#888888"); doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`${label} · Generado ${new Date().toLocaleString("es-DO")}`, M, 66);

    const rose = [183, 110, 121];
    const espresso = [44, 38, 33];
    autoTable(doc, {
      startY: 84,
      head: [["Resumen", "Monto"]],
      body: [
        ["Ventas totales", rd(data.total_sales)],
        ["Facturas / unidades", `${data.invoice_count} / ${data.units_sold}`],
        ["Delivery cobrado", rd(data.delivery_total)],
        ["ITBIS cobrado", rd(data.itbis_total)],
        ["Margen de ventas (bruto)", rd(data.gross_profit)],
        ["Gastos del mes", rd(data.total_expenses)],
        ["Ganancia neta", rd(data.net_profit)],
      ],
      theme: "grid", headStyles: { fillColor: rose, textColor: 255 },
      styles: { fontSize: 10, cellPadding: 5 }, columnStyles: { 1: { halign: "right" } },
      margin: { left: M, right: M },
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 16,
      head: [["Caja chica", "Monto"]],
      body: [
        ["Entradas (inversión / aportes)", rd(data.petty_cash_in)],
        ["Salidas (pagos / retiros)", rd(data.petty_cash_out)],
        ["Saldo actual", rd(data.petty_cash_balance)],
      ],
      theme: "grid", headStyles: { fillColor: rose, textColor: 255 },
      styles: { fontSize: 10, cellPadding: 5 }, columnStyles: { 1: { halign: "right" } },
      margin: { left: M, right: M },
    });
    if (data.expenses_by_category.length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 16,
        head: [["Gastos por categoría", "Total"]],
        body: data.expenses_by_category.map((c) => [catLabel(c.category), rd(c.total)]),
        theme: "grid", headStyles: { fillColor: rose, textColor: 255 },
        styles: { fontSize: 10, cellPadding: 5 }, columnStyles: { 1: { halign: "right" } },
        margin: { left: M, right: M },
      });
    }
    if (data.expenses.length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 16,
        head: [["Fecha", "Categoría", "Descripción", "Monto"]],
        body: data.expenses.map((e) => [e.date, catLabel(e.category), e.description || "—", rd(e.amount)]),
        theme: "striped", headStyles: { fillColor: espresso, textColor: 255 },
        styles: { fontSize: 9, cellPadding: 4 }, columnStyles: { 3: { halign: "right" } },
        margin: { left: M, right: M },
      });
    }
    doc.save(`cierre-mensual-${fileKey}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="inline-flex rounded-full border border-neutral-200 p-0.5" data-testid="cierre-mode-toggle">
            <button data-testid="cierre-mode-mes" onClick={() => setMode("mes")} className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.08em] transition ${mode === "mes" ? "bg-espresso text-cream" : "text-neutral-500"}`}>Por mes</button>
            <button data-testid="cierre-mode-rango" onClick={() => setMode("rango")} className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.08em] transition ${mode === "rango" ? "bg-espresso text-cream" : "text-neutral-500"}`}>Rango de fechas</button>
          </div>
          {mode === "mes" ? (
            <div><label className={labelCls}>Mes del cierre</label><input data-testid="cierre-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} /></div>
          ) : (
            <>
              <div><label className={labelCls}>Desde</label><input data-testid="cierre-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange((s) => ({ ...s, from: e.target.value }))} className={inputCls} /></div>
              <div><label className={labelCls}>Hasta</label><input data-testid="cierre-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange((s) => ({ ...s, to: e.target.value }))} className={inputCls} /></div>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button data-testid="cierre-excel" onClick={exportXlsx} className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition"><FileSpreadsheet size={14} /> Excel</button>
          <button data-testid="cierre-pdf" onClick={exportPdf} className="inline-flex items-center gap-2 border border-espresso text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-espresso hover:text-cream transition"><FileText size={14} /> PDF</button>
        </div>
      </div>

      {compare.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6" data-testid="cierre-compare-3m">
          <h2 className="font-medium mb-4">Comparativo de ganancia neta — últimos 3 meses</h2>
          <div className="grid grid-cols-3 gap-3">
            {compare.map((mm, idx) => {
              const prev = idx > 0 ? compare[idx - 1].net_profit : null;
              const diff = prev !== null ? mm.net_profit - prev : null;
              const pct = prev ? (diff / Math.abs(prev)) * 100 : null;
              const up = diff !== null && diff >= 0;
              return (
                <div key={mm.month} data-testid={`cierre-compare-${mm.month}`} className="border border-neutral-100 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400">{mm.label}</p>
                  <p className={`text-lg font-serif mt-1 ${mm.net_profit >= 0 ? "text-espresso" : "text-red-600"}`}>{rd(mm.net_profit)}</p>
                  {diff === null ? (
                    <p className="text-[11px] text-neutral-300 mt-1">— base</p>
                  ) : (
                    <p className={`text-[11px] mt-1 inline-flex items-center gap-1 ${up ? "text-emerald-600" : "text-rose"}`}>
                      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {up ? "+" : "−"}{rd(Math.abs(diff))}{pct !== null && isFinite(pct) ? ` (${up ? "+" : "−"}${Math.abs(pct).toFixed(0)}%)` : ""}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat l="Ventas totales" v={rd(data.total_sales)} sub={`${data.invoice_count} facturas · ${data.units_sold} uds`} />
            <Stat l="Delivery cobrado" v={rd(data.delivery_total)} />
            <Stat l="Margen de ventas" v={rd(data.gross_profit)} />
            <Stat l="Gastos del período" v={rd(data.total_expenses)} accent="rose" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className={`rounded-xl p-6 text-cream ${data.net_profit >= 0 ? "bg-emerald-700" : "bg-red-600"}`} data-testid="cierre-net-profit-card">
              <p className="text-[11px] uppercase tracking-wider opacity-70 flex items-center gap-1.5">{data.net_profit >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} Ganancia neta · {label}</p>
              <p className="text-4xl font-serif mt-2" data-testid="cierre-net-profit">{rd(data.net_profit)}</p>
              <p className="text-xs opacity-70 mt-2">Margen de ventas ({rd(data.gross_profit)}) − Gastos ({rd(data.total_expenses)})</p>
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <h2 className="font-medium mb-3 flex items-center gap-2"><Wallet size={16} className="text-rose" /> Caja chica</h2>
              <div className="space-y-2 text-sm">
                <Row l="Entradas (inversión)" v={`+ ${rd(data.petty_cash_in)}`} cls="text-emerald-600" />
                <Row l="Salidas (pagos/retiros)" v={`− ${rd(data.petty_cash_out)}`} cls="text-rose" />
                <div className="flex justify-between border-t border-neutral-100 pt-2 mt-1"><span className="font-medium">Saldo actual</span><span className="font-serif text-lg" data-testid="cierre-pettycash-balance">{rd(data.petty_cash_balance)}</span></div>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <h2 className="font-medium mb-3">Gastos por categoría</h2>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {data.expenses_by_category.length ? data.expenses_by_category.map((c, i) => (
                  <Row key={i} l={catLabel(c.category)} v={rd(c.total)} />
                )) : <p className="text-neutral-400 text-sm py-4 text-center">Sin gastos este mes.</p>}
              </div>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-6" data-testid="cierre-trend-chart">
            <h2 className="font-medium mb-1">Ventas vs. Gastos — últimos 6 meses</h2>
            <p className="text-xs text-neutral-400 mb-4">Compara los ingresos por ventas con los gastos y la ganancia neta mes a mes.</p>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={trend} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={{ stroke: "#eee" }} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
                <Tooltip formatter={(v, n) => [rd(v), n]} contentStyle={{ borderRadius: 10, border: "1px solid #eee", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sales" name="Ventas" fill="#2C2621" radius={[4, 4, 0, 0]} maxBarSize={34} />
                <Bar dataKey="expenses" name="Gastos" fill="#B76E79" radius={[4, 4, 0, 0]} maxBarSize={34} />
                <Line type="monotone" dataKey="net_profit" name="Ganancia neta" stroke="#047857" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ l, v, sub, accent }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    <p className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</p>
    <p className={`text-2xl font-serif mt-1 ${accent === "rose" ? "text-rose" : "text-espresso"}`}>{v}</p>
    {sub && <p className="text-[11px] text-neutral-400 mt-1">{sub}</p>}
  </div>
);
const Row = ({ l, v, cls }) => <div className="flex justify-between text-sm"><span className="text-neutral-600">{l}</span><span className={`font-medium ${cls || ""}`}>{v}</span></div>;
